// @ts-nocheck
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/query-client";
import { useChatPopup } from "@/hooks/use-chat-popup";
import { useMobileFabItem, MOBILE_FAB_PANEL_BOTTOM } from "@/components/layout/mobile-fab";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MessageCircle, Plus, ChevronDown, Search, X, Bot } from "lucide-react";
import type { ChatSpace, User as UserType, ChatAgent } from "@shared/schema";
import { filterUnknownSpaces, sortSpacesUnreadFirst, formatRelativeTime, getChatSpaceDisplayName } from "@/lib/chat-utils";

interface SpaceParticipant {
  userId: string;
  name: string;
  profileImageUrl: string | null;
  isAgent?: boolean;
}

interface LastMessagePreview {
  text: string | null;
  senderName: string;
  createdAt: string;
}

interface ChatSpaceWithUnread extends ChatSpace {
  unreadCount?: number;
  hasMention?: boolean;
  ownerId?: string | null;
  participants?: SpaceParticipant[];
  lastMessage?: LastMessagePreview;
}


function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
}

export function GlobalChatButton({ claimId }: { claimId?: string } = {}) {
  const { openChatPopup } = useChatPopup();
  const [, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { data: chatSpaces = [] } = useQuery<ChatSpaceWithUnread[]>({
    queryKey: ["/api/chat/spaces"],
  });

  const validSpaces = sortSpacesUnreadFirst(filterUnknownSpaces(chatSpaces));

  const { data: currentUser } = useQuery<UserType>({
    queryKey: ["/api/auth/user"],
  });

  const { data: allUsers = [] } = useQuery<UserType[]>({
    queryKey: ["/api/users/chat-participants"],
  });

  const { data: chatAgentsList = [] } = useQuery<ChatAgent[]>({
    queryKey: ["/api/chat-agents"],
  });

  // Dedupe agent DM spaces — show only the most recent per agent
  const dedupedSpaces = (() => {
    const seenAgents = new Set<string>();
    return validSpaces.filter((s) => {
      const agentP = s.spaceType === "DIRECT_MESSAGE"
        ? (s.participants ?? []).find((p) => p.isAgent)
        : null;
      if (!agentP) return true;
      const key = agentP.agentId ?? agentP.userId;
      if (seenAgents.has(key)) return false;
      seenAgents.add(key);
      return true;
    });
  })();

  const filteredSpaces = dedupedSpaces.filter((s) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const name = getChatSpaceDisplayName(s, s.participants ?? [], currentUser?.id).toLowerCase();
    return name.includes(q);
  });

  const filteredUsers = allUsers
    .filter((u) => u.id !== currentUser?.id)
    .filter((u) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return `${u.firstName ?? ""} ${u.lastName ?? ""}`.toLowerCase().includes(q);
    });

  const filteredAgents = chatAgentsList.filter((a) => {
    if (!searchQuery) return true;
    return a.displayName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const createDmMutation = useMutation({
    mutationFn: async (target: { targetUserId?: string; targetAgentId?: string }) => {
      const res = await apiRequest("POST", "/api/chat/dm", {
        ...target,
        ...(claimId && target.targetAgentId ? { claimId } : {}),
      });
      return res.json();
    },
    onSuccess: (space: any, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat/spaces"] });
      setIsOpen(false);
      setShowNewDm(false);
      if (variables.targetAgentId) {
        openChatPopup(space.id, `/chat/agent/${space.id}`, { width: 820, height: 700 });
      } else {
        openChatPopup(space.id);
      }
    },
  });

  const totalUnread = validSpaces.reduce((sum, s) => sum + (s.unreadCount || 0), 0);

  // Below lg the mobile FAB menu opens the panel instead of the pill.
  const inMobileFab = useMobileFabItem("chat", () => setIsOpen((v) => !v), { badge: totalUnread });

  const handleSpaceClick = (space: ChatSpaceWithUnread) => {
    const isAgent = space.spaceType === "DIRECT_MESSAGE" &&
      (space.participants ?? []).some((p) => p.isAgent);
    setIsOpen(false);
    if (isAgent) {
      openChatPopup(space.id, `/chat/agent/${space.id}`, { width: 820, height: 700 });
    } else {
      openChatPopup(space.id);
    }
  };

  // Close panel on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const buttonRight = 24;

  return (
    <>
      {/* Expanded panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="fixed z-50 flex flex-col bg-popover border border-border rounded-xl shadow-2xl overflow-hidden"
          style={
            inMobileFab
              ? {
                  right: 16,
                  bottom: MOBILE_FAB_PANEL_BOTTOM,
                  width: "min(360px, calc(100vw - 2rem))",
                  maxHeight: "min(480px, 60dvh)",
                }
              : {
                  right: buttonRight,
                  bottom: 68,
                  width: 360,
                  maxHeight: 480,
                }
          }
          data-testid="global-chat-popover"
        >
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
            <h3 className="text-sm font-semibold tracking-tight">Messaging</h3>
            <div className="flex items-center gap-1">
              <button
                className="h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                onClick={() => setShowNewDm(true)}
                title="New message"
                data-testid="global-chat-new-dm"
              >
                <Plus className="h-4 w-4" />
              </button>
              <button
                className="h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Search bar */}
          <div className="px-3 py-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full rounded-md bg-muted/50 border border-border pl-8 pr-7 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="global-chat-search"
              />
              {searchQuery && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setSearchQuery("")}
                  data-testid="global-chat-search-clear"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Panel body — always show all three sections */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {/* Spaces section */}
            {filteredSpaces.length > 0 && (
              <div>
                {filteredSpaces.map((space) => (
                  <SpaceRow
                    key={space.id}
                    space={space}
                    currentUserId={currentUser?.id}
                    onClick={() => handleSpaceClick(space)}
                  />
                ))}
              </div>
            )}

            {/* People section */}
            <div className="px-3 pt-3 pb-1">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-2">
                People
              </h4>
              {filteredUsers.length === 0 ? (
                <p className="text-xs text-muted-foreground px-1 pb-2">No users found</p>
              ) : (
                <div className="space-y-0.5">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                      onClick={() => createDmMutation.mutate({ targetUserId: user.id })}
                      disabled={createDmMutation.isPending}
                      data-testid={`people-user-${user.id}`}
                    >
                      <Avatar className="h-8 w-8">
                        {user.profileImageUrl ? <AvatarImage src={user.profileImageUrl} /> : null}
                        <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                          {getInitials(`${user.firstName || ""} ${user.lastName || ""}`)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {user.firstName} {user.lastName}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Agents section */}
            <div className="px-3 pt-1 pb-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-2">
                Agents
              </h4>
              {filteredAgents.length === 0 ? (
                <p className="text-xs text-muted-foreground px-1">No agents found</p>
              ) : (
                <div className="space-y-0.5">
                  {filteredAgents.map((agent) => (
                    <button
                      key={agent.id}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                      onClick={() => createDmMutation.mutate({ targetAgentId: agent.id })}
                      disabled={createDmMutation.isPending}
                      data-testid={`people-agent-${agent.id}`}
                    >
                      <Avatar className="h-8 w-8">
                        {agent.avatarUrl ? <AvatarImage src={agent.avatarUrl} /> : null}
                        <AvatarFallback className="text-xs font-medium bg-violet-100 text-violet-700">
                          {getInitials(agent.displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{agent.displayName}</div>
                        {agent.description && (
                          <div className="text-xs text-muted-foreground truncate">
                            {agent.description}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trigger button — rectangular pill */}
      {!inMobileFab && (
        <button
          ref={triggerRef}
          className="fixed bottom-5 z-50 flex items-center gap-2 h-10 pl-3 pr-3.5 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl active:scale-[0.97] transition-all select-none"
          style={{ right: buttonRight }}
          onClick={() => setIsOpen((v) => !v)}
          data-testid="global-chat-button"
        >
          <MessageCircle className="h-[18px] w-[18px]" />
          <span className="text-sm font-semibold leading-none">Chat</span>
          {totalUnread > 0 && (
            <span
              className="ml-0.5 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-primary"
              data-testid="global-chat-unread-badge"
            >
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      )}

      {/* New DM Dialog */}
      <Dialog open={showNewDm} onOpenChange={setShowNewDm}>
        <DialogContent data-testid="dialog-new-dm">
          <DialogHeader>
            <DialogTitle>New Direct Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {/* Users */}
            {allUsers
              .filter((u) => u.id !== currentUser?.id)
              .map((user) => (
                <button
                  key={user.id}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                  onClick={() => createDmMutation.mutate({ targetUserId: user.id })}
                  disabled={createDmMutation.isPending}
                  data-testid={`new-dm-user-${user.id}`}
                >
                  <Avatar className="h-9 w-9">
                    {user.profileImageUrl ? <AvatarImage src={user.profileImageUrl} /> : null}
                    <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                      {getInitials(`${user.firstName || ""} ${user.lastName || ""}`)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  </div>
                </button>
              ))}
            {/* Agents */}
            {chatAgentsList.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Agents
                  </span>
                </div>
                {chatAgentsList.map((agent) => (
                  <button
                    key={agent.id}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                    onClick={() => createDmMutation.mutate({ targetAgentId: agent.id })}
                    disabled={createDmMutation.isPending}
                    data-testid={`new-dm-agent-${agent.id}`}
                  >
                    <Avatar className="h-9 w-9">
                      {agent.avatarUrl ? <AvatarImage src={agent.avatarUrl} /> : null}
                      <AvatarFallback className="text-xs font-medium bg-violet-100 text-violet-700">
                        {getInitials(agent.displayName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{agent.displayName}</div>
                      {agent.description && (
                        <div className="text-xs text-muted-foreground truncate">
                          {agent.description}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─── Individual space row ─────────────────────────────────────────── */

function SpaceRow({
  space,
  currentUserId,
  onClick,
}: {
  space: ChatSpaceWithUnread;
  currentUserId?: string;
  onClick: () => void;
}) {
  const isDM = space.spaceType === "DIRECT_MESSAGE";
  const participants = space.participants ?? [];
  const displayName = getChatSpaceDisplayName(space, participants, currentUserId);
  const otherParticipant = isDM
    ? participants.find((p) => p.isAgent || (p.userId && p.userId !== currentUserId))
    : null;
  const isAgentRow = isDM && !!otherParticipant?.isAgent;

  const hasUnread = (space.unreadCount ?? 0) > 0;
  const lastMsg = space.lastMessage;

  // Build preview text
  let previewText = "";
  if (lastMsg?.text) {
    // For group chats, prefix with sender first name
    if (!isDM && lastMsg.senderName) {
      const firstName = lastMsg.senderName.split(" ")[0];
      previewText = `${firstName}: ${lastMsg.text}`;
    } else {
      previewText = lastMsg.text;
    }
  }

  const timeAgo = lastMsg?.createdAt
    ? formatRelativeTime(lastMsg.createdAt)
    : "";

  return (
    <button
      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/60 active:bg-muted transition-colors text-left group"
      onClick={onClick}
      data-testid={`global-chat-space-${space.id}`}
    >
      {/* Avatar */}
      <SpaceAvatar
        participants={participants}
        currentUserId={currentUserId}
        isDM={isDM}
      />

      {/* Name + preview */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span
            className={`text-[13px] truncate leading-tight flex items-center gap-1 ${
              hasUnread ? "font-bold text-foreground" : "font-medium text-foreground"
            }`}
          >
            {displayName}
            {isAgentRow && <Bot className="h-3 w-3 shrink-0 text-violet-500" />}
          </span>
          {timeAgo && (
            <span
              className={`text-[11px] shrink-0 leading-none ${
                hasUnread
                  ? "font-semibold text-primary"
                  : "text-muted-foreground"
              }`}
            >
              {timeAgo}
            </span>
          )}
        </div>
        {(previewText || !lastMsg) && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className={`text-[12px] truncate leading-snug ${
                hasUnread
                  ? "font-semibold text-foreground"
                  : "text-muted-foreground"
              }`}
            >
              {previewText || "No messages yet"}
            </span>
            {hasUnread && (
              <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
            )}
          </div>
        )}
      </div>
    </button>
  );
}

/* ─── Avatar component ─────────────────────────────────────────────── */

function SpaceAvatar({
  participants,
  currentUserId,
  isDM,
}: {
  participants: SpaceParticipant[];
  currentUserId?: string;
  isDM: boolean;
}) {
  const visible = isDM
    ? participants.filter((p) => p.userId !== currentUserId).slice(0, 1)
    : participants.slice(0, 2);

  // Single avatar (DM or single-participant group)
  if (visible.length <= 1) {
    const p = visible[0];
    if (p?.isAgent) {
      return (
        <div className="h-10 w-10 shrink-0 rounded-full bg-violet-100 flex items-center justify-center">
          <Bot className="h-5 w-5 text-violet-600" />
        </div>
      );
    }
    return (
      <Avatar className="h-10 w-10 shrink-0">
        {p?.profileImageUrl ? (
          <AvatarImage src={p.profileImageUrl} alt={p.name} />
        ) : null}
        <AvatarFallback className="text-xs font-semibold bg-primary/10 text-primary">
          {p ? getInitials(p.name) : "?"}
        </AvatarFallback>
      </Avatar>
    );
  }

  // Stacked avatars for groups (2 overlapping)
  return (
    <div className="relative h-10 w-10 shrink-0">
      <Avatar className="absolute bottom-0 left-0 h-7 w-7 border-2 border-popover z-[1]">
        {visible[0]?.profileImageUrl ? (
          <AvatarImage src={visible[0].profileImageUrl} alt={visible[0].name} />
        ) : null}
        <AvatarFallback className="text-[9px] font-semibold bg-primary/10 text-primary">
          {getInitials(visible[0].name)}
        </AvatarFallback>
      </Avatar>
      <Avatar className="absolute top-0 right-0 h-7 w-7 border-2 border-popover">
        {visible[1]?.profileImageUrl ? (
          <AvatarImage src={visible[1].profileImageUrl} alt={visible[1].name} />
        ) : null}
        <AvatarFallback className="text-[9px] font-semibold bg-muted text-muted-foreground">
          {getInitials(visible[1].name)}
        </AvatarFallback>
      </Avatar>
    </div>
  );
}
